import { useEffect, useState } from "react";
import { api } from "../api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const emptyForm = { name: "", body: "", category: "custom", meta_name: "", meta_language: "en_US", meta_enabled: false, has_document_header: false, has_image_header: false, named_params: false };

export default function Templates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);

  const load = async () => { const { data } = await api.get("/templates"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing) await api.put(`/templates/${editing.id}`, form);
      else await api.post("/templates", form);
      toast.success("Saved"); setOpen(false); setForm(emptyForm); setEditing(null); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/templates/${id}`); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Messaging</p>
          <h1 className="font-serif-elegant text-5xl mt-1">Templates</h1>
        </div>
        <Dialog open={open} onOpenChange={(v)=>{ setOpen(v); if(!v){setForm(emptyForm); setEditing(null);} }}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-[#984D38]" data-testid="new-template-btn"><Plus size={14} className="mr-2"/>New Template</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-serif-elegant text-2xl">{editing?"Edit":"New"} Template</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name</Label><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="tpl-name" /></div>
              <div><Label>Category</Label>
                <Select value={form.category} onValueChange={v=>setForm({...form,category:v})}>
                  <SelectTrigger data-testid="tpl-category"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="reminder">Payment reminder</SelectItem>
                    <SelectItem value="review">Review request</SelectItem>
                    <SelectItem value="broadcast">Broadcast</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Meta template name (approved by Meta)</Label><Input value={form.meta_name} onChange={e=>setForm({...form,meta_name:e.target.value})} placeholder="e.g. gaze_invoice" /></div>
              <div><Label>Meta language</Label><Input value={form.meta_language} onChange={e=>setForm({...form,meta_language:e.target.value})} placeholder="en_US" /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.meta_enabled} onChange={e=>setForm({...form,meta_enabled:e.target.checked})}/> Use this approved Meta template for sending</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.has_document_header} onChange={e=>setForm({...form,has_document_header:e.target.checked})}/> This template's Meta header is a Document (attaches the invoice PDF directly in WhatsApp)</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.named_params} onChange={e=>setForm({...form,named_params:e.target.checked})}/> The Meta template uses named variables like {"{{customer_name}}"} (not {"{{1}}"}). Use the same names in the body above, for example {"{customer_name}"}.</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.has_image_header} onChange={e=>setForm({...form,has_image_header:e.target.checked})}/> This template's Meta header is an Image (e.g. an offer photo)</label>
              <div><Label>Body / local preview</Label>
                <Textarea rows={6} value={form.body} onChange={e=>setForm({...form,body:e.target.value})} data-testid="tpl-body" />
                <p className="text-xs text-muted-foreground mt-1">Placeholders: {"{customer_name}"}, {"{business_name}"}, {"{invoice_number}"}, {"{invoice_amount}"}, {"{invoice_link}"}</p>
              </div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-primary hover:bg-[#984D38]" data-testid="tpl-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(t => (
          <Card key={t.id} className="p-5 bg-white border-border">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-serif-elegant text-xl">{t.name}</h3>
                <Badge variant="outline" className="mt-1 capitalize">{t.category}</Badge>{t.meta_enabled && <Badge variant="outline" className="mt-1 ml-2">Meta: {t.meta_name}</Badge>}
              </div>
              <div>
                <Button size="icon" variant="ghost" onClick={()=>{ setEditing(t); setForm({name:t.name,body:t.body,category:t.category,meta_name:t.meta_name||"",meta_language:t.meta_language||"en_US",meta_enabled:!!t.meta_enabled,has_document_header:!!t.has_document_header,has_image_header:!!t.has_image_header,named_params:!!t.named_params}); setOpen(true); }}><Pencil size={14}/></Button>
                <Button size="icon" variant="ghost" onClick={()=>del(t.id)}><Trash2 size={14} className="text-destructive"/></Button>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap">{t.body}</p>
          </Card>
        ))}
        {items.length===0 && <p className="text-muted-foreground">No templates yet.</p>}
      </div>
    </div>
  );
}
